import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function RouterPage(): Node {
  return html`
    <div>
      <h1>Router</h1>
      <p>Client-side routing with nested routes, dynamic parameters, and programmatic navigation.</p>

      <h2>Basic Setup</h2>
      ${CodeBlock(`import { Router, navigate, Link } from 'onefold';

const App = Router([
  { path: '/', view: () => Home() },
  { path: '/about', view: () => About() },
  { path: '/users/:id', view: (params) => UserProfile(params) },
], () => NotFound());`)}

      <h2>How It Works</h2>
      <p>The Router listens to <code>popstate</code> events (History API) and swaps the rendered view when the path changes. Only the matched route's view function is called — other routes remain unmounted.</p>

      ${Callout('The Router returns a single DOM Node. Mount it once at your app root — route changes swap content in-place without a full re-render.')}

      <h2>Route Definition</h2>
      <table>
        <tr><th>Property</th><th>Type</th><th>Description</th></tr>
        <tr><td><code>path</code></td><td>string</td><td>URL pattern to match. Supports <code>:param</code> segments.</td></tr>
        <tr><td><code>view</code></td><td>(params, outlet?) => Node</td><td>Render function called when route matches.</td></tr>
        <tr><td><code>children</code></td><td>RouteDefinition[]</td><td>Nested child routes (optional).</td></tr>
      </table>

      <h2>Full Example</h2>
      ${CodeBlock(`import { Router, navigate, Link, html, mount } from 'onefold';

function Home(): Node {
  return html\`<h2>Welcome Home</h2>\`;
}

function About(): Node {
  return html\`<h2>About Us</h2>\`;
}

function NotFound(): Node {
  return html\`<h2>404 - Not Found</h2>\`;
}

function App(): Node {
  return html\`
    <div>
      <nav>
        \${Link('/', html\`<span>Home</span>\`)}
        \${Link('/about', html\`<span>About</span>\`)}
      </nav>
      \${Router([
        { path: '/', view: () => Home() },
        { path: '/about', view: () => About() },
      ], () => NotFound())}
    </div>
  \`;
}

mount(App(), document.getElementById('app')!);`)}

      <h2>Try It</h2>
      <p>Click the nav links to switch routes. Uses hash mode so it works inside the playground:</p>

      ${Playground(`import { html, mount, Router, navigate, currentRoute, Link, configureRouter } from 'onefold';

// Use hash mode (required for playground/iframe environments)
configureRouter({ hash: true });

function Home() {
  return html\`
    <div style="padding:16px">
      <h2>Home</h2>
      <p>Welcome to the home page. Click the nav links above to navigate.</p>
      <button onclick=\${() => navigate('/about')} style="margin-top:8px">
        Go to About (programmatic)
      </button>
    </div>
  \`;
}

function About() {
  return html\`
    <div style="padding:16px">
      <h2>About</h2>
      <p>This is the about page. The router swaps content in-place.</p>
    </div>
  \`;
}

function UserProfile(params) {
  return html\`
    <div style="padding:16px">
      <h2>User: \${params.id}</h2>
      <p>Dynamic route parameter captured from the URL.</p>
    </div>
  \`;
}

function NotFound() {
  return html\`<div style="padding:16px"><h2>404 — Not Found</h2></div>\`;
}

function App() {
  return html\`
    <div>
      <nav style="display:flex;gap:12px;padding:12px 16px;background:#f1f5f9;border-radius:8px;margin-bottom:16px">
        \${Link('/', 'Home', () => currentRoute() === '/' ? 'font-weight:bold;color:#4f46e5' : 'color:#333')}
        \${Link('/about', 'About', () => currentRoute() === '/about' ? 'font-weight:bold;color:#4f46e5' : 'color:#333')}
        \${Link('/users/42', 'User 42', () => currentRoute() === '/users/42' ? 'font-weight:bold;color:#4f46e5' : 'color:#333')}
        \${Link('/users/99', 'User 99', () => currentRoute() === '/users/99' ? 'font-weight:bold;color:#4f46e5' : 'color:#333')}
      </nav>
      <div style="border:1px solid #e5e7eb;border-radius:8px;min-height:120px">
        \${Router([
          { path: '/', view: () => Home() },
          { path: '/about', view: () => About() },
          { path: '/users/:id', view: (params) => UserProfile(params) },
        ], () => NotFound())}
      </div>
      <p style="margin-top:8px;font-size:12px;color:#666">Current route: \${() => currentRoute()}</p>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'Router with Navigation, Dynamic Params & Active Links')}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/routing/nested">Nested Routes</a> — share layouts across related pages</li>
        <li><a href="/routing/navigate">Navigate</a> — programmatic navigation from code</li>
        <li><a href="/routing/params">Dynamic Params</a> — capture URL segments as parameters</li>
      </ul>
    </div>
  `;
}
